import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AuthenticatedUser, Role } from '@/types/api'

/**
 * Client-side auth state. The server session is the authority; this holds who
 * the router and the chrome think is signed in, so guards do not have to await
 * a query on every navigation.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthenticatedUser | null>(null)
  const hasCheckedSession = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const role = computed<Role | null>(() => user.value?.role ?? null)

  // Managers and admins; use for pages a plain user should not operate.
  const isManagerOrAdmin = computed(() => role.value === 'manager' || role.value === 'admin')
  const isAdmin = computed(() => role.value === 'admin')

  const setUser = (next: AuthenticatedUser | null): void => {
    user.value = next
    hasCheckedSession.value = true
  }

  const clear = (): void => {
    user.value = null
    hasCheckedSession.value = true
  }

  return {
    user,
    hasCheckedSession,
    isAuthenticated,
    role,
    isManagerOrAdmin,
    isAdmin,
    setUser,
    clear,
  }
})
